import { accessibleRealismId, type RealismId } from '../physics/realism';
import { isBoolean, isFiniteNumber, orNull, sanitize } from './validate';

export type GhostMode = 'best' | 'reference' | 'rival' | 'none';
export type LapLength = 'standard' | 'short';
export type RendererMode = '2d' | '3d';
export type Camera3dMode = 'chase' | 'follow' | 'overhead' | 'cockpit';
/**
 * Which way "up" points in the plain overhead view — the 2D top-down renderer
 * and the 3D `overhead` camera both read it.
 *
 * `track-up` is the historical fixed-north view: the circuit never rotates, so
 * a braking point is always in the same place on screen, which is the whole
 * reason the game defaults to it. `kart-up` rotates the world so the kart's
 * nose always points to the top of the screen — easier to read where you are
 * going, harder to memorise where you are. Forced back to `track-up` while
 * reduced motion is on, since a continuously rotating world is a motion
 * trigger.
 */
export type OverheadOrientation = 'track-up' | 'kart-up';
/** One channel of the replay Data dock. Each is independently add/dropable —
 *  see docs on `telemetryLayers` below. */
export type TelemetryLayer = 'summary' | 'traces' | 'circle' | 'readout';

export interface Settings {
  realism: RealismId;
  livery: string;
  /** Centerline tinted by the solved speed profile. */
  referenceLine: boolean;
  ghost: GhostMode;
  sound: boolean;
  volume: number;
  /** Damps camera shake and cuts particle counts. */
  reducedMotion: boolean;
  /** Swaps the braking-zone ramp for a blue→yellow scale that survives
   *  red-green colour blindness. */
  colorblind: boolean;
  /** Tyres warm up and wear out. Off by default — see tyres.ts. */
  tyreModel: boolean;
  lapLength: LapLength;
  /**
   * Per-assist overrides. Null means "whatever the realism mode says", which is
   * what picking a mode resets them to; once the player moves one of these it
   * holds an explicit value, including an explicit off, until they pick a mode
   * again. Writing null on *uncheck* is what made the off position a no-op in
   * any mode whose default was on.
   */
  brakeAssistOverride: boolean | null;
  stabilityOverride: number | null;
  /**
   * Brake for the player into every corner, so the pedal is never required.
   * Unlike the two overrides above this is not a realism-mode assist — it adds
   * brake input rather than shaping input already given — so it is its own
   * setting. It changes lap times: records shard on it and it is never
   * submitted to the leaderboard. See game/autobrake.ts.
   */
  autoBrake: boolean;
  /** UI and HUD scale as a percentage. */
  textScale: number;
  /** Beep as you reach each corner's ideal braking point. */
  brakeCue: boolean;
  /** Drill linked corners as a complex rather than in isolation. */
  drillApproach: boolean;
  /** Falls back to '2d' automatically if WebGL is unavailable. */
  renderer: RendererMode;
  camera3d: Camera3dMode;
  /** Overhead-view rotation; see OverheadOrientation. */
  overheadOrientation: OverheadOrientation;
  /** AI opponents on a Grand Prix grid. */
  gpGridSize: number;
  /** Grand Prix format: pace-estimated AI grid, elimination qualifying then
   *  that grid, or a real friends' room. */
  gpMode: 'solo' | 'quali' | 'room';
  /** Which replay Data channels are docked on screen. The kart always renders
   *  screen-centre (see Camera.applyTransform), so this starts light — only
   *  the whole-lap summary strip, which is thin enough to dock clear of it —
   *  and the rest are opt-in per layer rather than one big block. */
  telemetryLayers: TelemetryLayer[];
}

export const DEFAULT_SETTINGS: Settings = {
  realism: 'arcade',
  livery: 'vantari',
  referenceLine: false,
  ghost: 'best',
  sound: true,
  volume: 0.7,
  reducedMotion: false,
  colorblind: false,
  tyreModel: false,
  lapLength: 'standard',
  brakeAssistOverride: null,
  stabilityOverride: null,
  autoBrake: false,
  textScale: 100,
  brakeCue: false,
  drillApproach: true,
  renderer: '2d',
  camera3d: 'chase',
  overheadOrientation: 'track-up',
  gpGridSize: 7,
  gpMode: 'solo',
  telemetryLayers: ['summary'],
};

const KEY = 'karting-hotlap:settings';

let cache: Settings | null = null;

export function loadSettings(): Settings {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY);
    // Merge over the defaults so a settings file written by an older build
    // gains new keys rather than leaving them undefined. localStorage is
    // user-editable and can hold a stale or hand-edited blob, so each field
    // is validated against the default's type before it is accepted — e.g. a
    // `telemetryLayers: null` must not reach the `.includes(...)` call sites
    // that assume it is always an array.
    cache = raw
      ? sanitize(DEFAULT_SETTINGS, JSON.parse(raw) as Partial<Settings>, {
          brakeAssistOverride: orNull(isBoolean),
          stabilityOverride: orNull(isFiniteNumber),
        })
      : { ...DEFAULT_SETTINGS };
  } catch {
    cache = { ...DEFAULT_SETTINGS };
  }
  // A settings file that still names a shelved mode (Sport/Sim) is pulled back
  // to one that can currently be picked.
  cache.realism = accessibleRealismId(cache.realism);
  return cache;
}

export function updateSettings(patch: Partial<Settings>): Settings {
  const next = { ...loadSettings(), ...patch };
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode or quota; the session still honours the change */
  }
  return next;
}

/** Reduced motion scales shake and particle counts rather than disabling them. */
export function motionScale(s: Settings): number {
  return s.reducedMotion ? 0 : 1;
}
